import create from "zustand";
import { Dashboard } from "api/model/dashboard/Dashboard";
import { DashboardTabModel } from "api/model/dashboard/DashboardTab";

interface DashboardState {
  dashboard: Dashboard | undefined;
  tab: DashboardTabModel | undefined;
  isEditMode: boolean;
  isGroupReorderEnabled: boolean;
  isComponentsReorderEnabled: boolean;
  setDashboard: (dashboard: Dashboard) => void;
  setTab: (tab: DashboardTabModel) => void;
  setIsEditMode: (isEditMode: boolean) => void;
  getTab: (tabId: number) => DashboardTabModel | undefined;
  updateComponent: (groupId: string, componentId: string, config: any, tabId: number) => void;
  removeComponent: (groupId: string, componentId: string, tabId: number) => void;
  moveComponent: (groupId: string, componentId: string, targetGroupId: number, tabId: number) => void;
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => void;
  setIsComponentsReorderEnabled: (isComponentsReorderEnabled: boolean) => void;
}

export const useDashboardStore = create<DashboardState>((set, get) => ({
  dashboard: undefined,
  tab: undefined,
  isEditMode: false,
  isGroupReorderEnabled: false,
  isComponentsReorderEnabled: false,
  setDashboard: (dashboard: Dashboard) => set({ dashboard }),
  setTab: (tab: any) => set({ tab }),
  setIsEditMode: (isEditMode: boolean) => set({ isEditMode }),
  getTab: (tabId: number) => {
    const state = get();
    return state.dashboard?.dashboardTabsJs?.[tabId];
  },
  setIsGroupReorderEnabled: (isGroupReorderEnabled: boolean) => set({ isGroupReorderEnabled }),
  setIsComponentsReorderEnabled: (isComponentsReorderEnabled: boolean) =>
    set({ isComponentsReorderEnabled }),
  updateComponent: (groupId: string, componentId: string | string, config: any, tabId: number) =>
    set(state => {
      const { dashboard } = state;
      if (!dashboard) return state;

      const updatedDashboard = {
        ...dashboard,
        dashboardTabsJs: [...dashboard.dashboardTabsJs],
      };

      const tab = updatedDashboard.dashboardTabsJs?.[tabId];
      if (!tab) return state;

      const group = tab.componentGroups?.find(g => g.id === groupId);
      if (!group) return state;

      const { components } = group;
      if (!components) return state;

      const compIndex = components.findIndex(component => component.id === componentId);
      if (compIndex === -1) return state;

      components[compIndex] = config;

      return { dashboard: updatedDashboard };
    }),

  moveComponent: (groupId: string, componentId: string, targetGroupId: number, tabId: number) =>
    set(state => {
      const { dashboard } = state;
      if (!dashboard) return state;

      const updatedDashboard = { ...dashboard };

      const tab = updatedDashboard.dashboardTabsJs?.[tabId];
      if (!tab) return state;

      const sourceGroup = tab.componentGroups?.find(g => g.id === groupId);
      const targetGroup = tab.componentGroups?.find(g => g.id === targetGroupId.toString());
      if (!sourceGroup || !targetGroup) return state;

      const { components } = sourceGroup;
      if (!components) return state;

      const compIndex = components.findIndex(component => component.id === componentId);
      if (compIndex === -1) return state;

      const [movedComponent] = components.splice(compIndex, 1);

      targetGroup.components.push(movedComponent);

      return { dashboard: updatedDashboard };
    }),

  removeComponent: (groupId: string, componentId: string, tabId: number) =>
    set(state => {
      const { dashboard } = state;
      if (!dashboard) return state;

      const updatedDashboard = {
        ...dashboard,
        dashboardTabsJs: dashboard.dashboardTabsJs.map((tab, tIndex) =>
          tIndex === tabId
            ? {
                ...tab,
                componentGroups: tab.componentGroups?.map(group =>
                  group.id === groupId
                    ? {
                        ...group,
                        components: group.components ? [...group.components] : [],
                      }
                    : group
                ),
              }
            : tab
        ),
      };

      const tab = updatedDashboard.dashboardTabsJs?.[tabId];
      if (!tab) return state;

      const group = tab.componentGroups?.find(gr => gr.id === groupId);
      if (!group) return state;

      const { components } = group;
      if (!components) return state;

      const compIndex = components.findIndex(component => component.id === componentId);
      if (compIndex === -1) return state;

      components.splice(compIndex, 1);

      return { dashboard: updatedDashboard };
    }),
}));

export const setEditMode = (isEdit: boolean) => {
  useDashboardStore.setState({ isEditMode: isEdit });
};

export const setDashboard = (dashboard: Dashboard | undefined) => {
  useDashboardStore.setState({ dashboard });
};

export default useDashboardStore;
